import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ChevronRight, ShieldAlert, BookOpen, Info, Mail, Instagram, Facebook, Youtube, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { MarketingLayout, Section, Eyebrow, TrialButton, useSeo } from "@/components/marketing/MarketingLayout";
import { FoundingBadge } from "@/pages/marketing/Home";
import { SeeInActionTour } from "@/pages/marketing/DemoTour";
import { publicService } from "@/services/publicService";
import { PILLARS, FAQS, PLATFORM_FLOW, RESA_CAPABILITIES, HOW_IT_WORKS } from "@/config/marketing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const Hero = ({ eyebrow, title, sub }) => (
  <div className="bg-brand-navy text-white">
    <Section className="py-16 sm:py-20">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h1 className="mt-4 max-w-3xl font-brand text-4xl font-bold leading-tight sm:text-5xl">{title}</h1>
      {sub && <p className="mt-4 max-w-2xl text-lg text-white/70">{sub}</p>}
    </Section>
  </div>
);

/* ---------------- Platform ---------------- */
export function PlatformPage() {
  useSeo({ title: "Platform", path: "/platform", description: "How AgentHQ connects your relationships, pipeline, transactions, marketing, compliance, goals, and RESA into one operating system." });
  return (
    <MarketingLayout>
      <Hero eyebrow="The platform" title="One operating system for your whole business."
        sub="AgentHQ connects the moving pieces so your business flows from first contact to closed deal — and beyond." />
      <Section className="py-16" data-testid="platform-flow">
        <h2 className="font-brand text-2xl font-bold text-brand-navy">The relationship-to-revenue flow</h2>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {PLATFORM_FLOW.map((s, i) => (
            <span key={s} className="flex items-center gap-2">
              <span className="rounded-full border border-brand-navy/15 bg-white px-4 py-2 text-sm font-medium text-brand-navy">{s}</span>
              {i < PLATFORM_FLOW.length - 1 && <ChevronRight className="h-4 w-4 text-brand-gold" />}
            </span>
          ))}
        </div>
        <p className="mt-4 max-w-2xl text-sm text-charcoal/60">Alongside Tasks, Calendar, Marketing, Compliance, Goals, the Dashboard, and RESA — all aware of the same business context.</p>
      </Section>
      <div className="bg-brand-light-gray"><Section className="py-16">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p) => { const Icon = p.icon; return (
            <div key={p.key} className="rounded-2xl border border-black/10 bg-white p-6">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-navy text-brand-gold"><Icon className="h-5 w-5" /></div>
              <p className="mt-4 font-brand text-lg font-bold text-brand-navy">{p.label}</p>
              <p className="text-sm text-charcoal/60">{p.tagline}</p>
            </div>); })}
        </div>
      </Section></div>
      <SeeInAction />
      <FinalCta />
    </MarketingLayout>
  );
}

/* ---------------- Features ---------------- */
export function FeaturesPage() {
  useSeo({ title: "Features", path: "/features", description: "Explore AgentHQ features across RUN, SELL, MARKET, PROTECT and GROW — plus RESA, the built-in Real Estate Strategic Assistant." });
  return (
    <MarketingLayout>
      <Hero eyebrow="Features" title="Everything your business runs on."
        sub="Organized around the five areas of your business, plus RESA. Every feature below is available today." />
      <Section className="space-y-10 py-16" data-testid="features-list">
        {PILLARS.map((p) => { const Icon = p.icon; return (
          <div key={p.key} className="grid gap-6 border-b border-black/5 pb-10 last:border-0 md:grid-cols-[220px_1fr]">
            <div>
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-navy text-brand-gold"><Icon className="h-5 w-5" /></div>
              <p className="mt-3 font-brand text-xl font-bold text-brand-navy">{p.label}</p>
              <p className="text-sm text-charcoal/60">{p.tagline}</p>
            </div>
            <div>
              <ul className="grid gap-2 sm:grid-cols-2">
                {p.items.map((i) => <li key={i} className="flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm text-charcoal/80"><Check className="h-4 w-4 text-brand-gold" /> {i}</li>)}
              </ul>
              {p.note && <p className="mt-3 flex items-start gap-1.5 text-xs text-charcoal/50"><Info className="mt-0.5 h-3.5 w-3.5" /> {p.note}</p>}
            </div>
          </div>); })}
        <div className="grid gap-6 md:grid-cols-[220px_1fr]">
          <div>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-gold text-brand-navy font-brand text-lg font-bold">R</div>
            <p className="mt-3 font-brand text-xl font-bold text-brand-navy">RESA</p>
            <p className="text-sm text-charcoal/60">Real Estate Strategic Assistant.</p>
          </div>
          <ul className="grid gap-2 sm:grid-cols-2">
            {RESA_CAPABILITIES.map((c) => <li key={c} className="flex items-start gap-2 rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm text-charcoal/80"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" /> {c}</li>)}
          </ul>
        </div>
      </Section>
      <FinalCta />
    </MarketingLayout>
  );
}

/* ---------------- RESA ---------------- */
export function ResaPage() {
  useSeo({ title: "RESA — Real Estate Strategic Assistant", path: "/resa", description: "RESA is the intelligence built into AgentHQ. It understands your authorized business context and prepares next actions for your approval — it never acts on its own." });
  return (
    <MarketingLayout>
      <Hero eyebrow="Real Estate Strategic Assistant" title="Meet RESA."
        sub="Intelligence built into AgentHQ — not a detached chatbot. RESA works from your authorized business context and always prepares actions for you to approve." />
      <Section className="py-16" data-testid="resa-capabilities">
        <div className="grid gap-4 sm:grid-cols-2">
          {RESA_CAPABILITIES.map((c) => (
            <div key={c} className="flex items-start gap-3 rounded-2xl border border-black/10 bg-white p-5"><Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" /><p className="text-sm text-charcoal/80">{c}</p></div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl border border-brand-navy/15 bg-brand-light-gray p-6" data-testid="resa-boundaries">
          <p className="flex items-center gap-2 font-semibold text-brand-navy"><ShieldAlert className="h-5 w-5 text-brand-gold" /> Clear boundaries, always</p>
          <p className="mt-2 text-sm text-charcoal/70">RESA never autonomously sends messages, publishes content, moves money, changes billing, deletes records, or makes legal or compliance decisions. Every action is proposed for your explicit approval and runs through the same permissions as the rest of AgentHQ.</p>
        </div>
      </Section>
      <FinalCta />
    </MarketingLayout>
  );
}

/* ---------------- Pricing ---------------- */
export function PricingPage() {
  useSeo({ title: "Pricing", path: "/pricing", description: "AgentHQ is $39/month or $390/year with a 14-day free trial. Founding Agents lock $19/month while spots remain." });
  const [cfg, setCfg] = useState(null);
  useEffect(() => { publicService.siteConfig().then(setCfg).catch(() => setCfg(null)); }, []);
  const foundingOpen = cfg?.founding?.enrollment_open && cfg?.founding?.remaining > 0;
  return (
    <MarketingLayout>
      <Hero eyebrow="Pricing" title="Simple, honest pricing." sub="One plan with everything. No freemium, no confusing tiers." />
      <Section className="py-16" data-testid="pricing">
        {cfg && !cfg.live_billing && (
          <div className="mb-6 rounded-xl border border-brand-gold/40 bg-brand-gold/10 px-4 py-3 text-sm text-brand-navy" data-testid="pricing-test-mode">
            <Info className="mr-1.5 inline h-4 w-4" /> Billing is in test mode during our current rollout — no real charges are processed yet.
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Standard */}
          <div className="rounded-2xl border border-black/10 bg-white p-8" data-testid="plan-standard">
            <p className="font-brand text-xl font-bold text-brand-navy">AgentHQ</p>
            <p className="mt-2"><span className="font-brand text-4xl font-bold text-brand-navy">$39</span><span className="text-charcoal/60">/month</span></p>
            <p className="text-sm text-charcoal/60">or $390/year</p>
            <ul className="mt-5 space-y-2 text-sm text-charcoal/80">
              {["Full AgentHQ platform (RUN · SELL · MARKET · PROTECT · GROW)", "RESA — Real Estate Strategic Assistant", "14-day free trial", "Cancel anytime"].map((f) => <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" /> {f}</li>)}
            </ul>
            <p className="mt-4 text-xs text-charcoal/50">Payment method required. Billing begins after your trial unless you cancel.</p>
            <div className="mt-6"><TrialButton className="w-full justify-center bg-brand-navy text-white hover:bg-brand-deep-blue" /></div>
          </div>
          {/* Founding */}
          <div className={`rounded-2xl border p-8 ${foundingOpen ? "border-brand-gold bg-brand-navy text-white" : "border-black/10 bg-white opacity-70"}`} data-testid="plan-founding">
            <div className="flex items-center justify-between">
              <p className="font-brand text-xl font-bold">Founding Agent</p>
              {foundingOpen && <FoundingBadge compact />}
            </div>
            {foundingOpen ? (
              <>
                <p className="mt-2"><span className="font-brand text-4xl font-bold">$19</span><span className="text-white/60">/month</span></p>
                <p className="text-sm text-white/60">Limited to the first 100 qualifying paid Founding Agents.</p>
                <ul className="mt-5 space-y-2 text-sm text-white/85">
                  {["Everything in AgentHQ", "Locked $19/mo rate", "Rate stays locked while your subscription remains continuously active"].map((f) => <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" /> {f}</li>)}
                </ul>
                <p className="mt-4 text-xs text-white/50">A Founding number is secured only after successful qualifying paid activation. Cancel and let it expire and the Founding rate is forfeited.</p>
                <div className="mt-6"><TrialButton founding className="w-full justify-center bg-brand-gold text-brand-navy hover:bg-brand-soft-gold" /></div>
              </>
            ) : (
              <p className="mt-3 text-sm text-charcoal/60">All 100 Founding Agent spots have been claimed. Standard pricing applies — start your 14-day trial above.</p>
            )}
          </div>
        </div>
      </Section>
      <div className="bg-brand-light-gray"><Section className="py-14"><FaqInline limit={5} /></Section></div>
    </MarketingLayout>
  );
}

/* ---------------- How It Works ---------------- */
export function HowItWorksPage() {
  useSeo({ title: "How It Works", path: "/how-it-works", description: "Three steps: set up your business, bring it together, and run it from AgentHQ." });
  return (
    <MarketingLayout>
      <Hero eyebrow="How it works" title="Up and running in three steps." />
      <Section className="py-16">
        <div className="space-y-5">
          {HOW_IT_WORKS.map((s) => (
            <div key={s.n} className="flex items-start gap-5 rounded-2xl border border-black/10 bg-white p-6" data-testid={`how-step-${s.n}`}>
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-brand-navy font-brand text-xl font-bold text-brand-gold">{s.n}</div>
              <div><p className="font-brand text-lg font-bold text-brand-navy">{s.title}</p><p className="mt-1 text-charcoal/70">{s.body}</p></div>
            </div>
          ))}
        </div>
      </Section>
      <FinalCta />
    </MarketingLayout>
  );
}

/* ---------------- Resources ---------------- */
export function ResourcesPage() {
  useSeo({ title: "Resources", path: "/resources", description: "Guides, business resources, and product updates for AgentHQ — coming soon." });
  const cats = [["Guides", "Practical guides for running your real estate business."], ["Business Resources", "Templates and playbooks to help you grow."], ["Product Updates", "What's new in AgentHQ."]];
  return (
    <MarketingLayout>
      <Hero eyebrow="Resources" title="Guides & business resources." />
      <Section className="py-16" data-testid="resources">
        <div className="grid gap-5 md:grid-cols-3">
          {cats.map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-black/10 bg-white p-6">
              <BookOpen className="h-7 w-7 text-brand-gold" />
              <p className="mt-3 font-semibold text-brand-navy">{t}</p>
              <p className="mt-1 text-sm text-charcoal/60">{d}</p>
              <span className="mt-4 inline-block rounded-full bg-brand-light-gray px-3 py-1 text-xs font-medium text-charcoal/60">Coming soon</span>
            </div>
          ))}
        </div>
      </Section>
      <FinalCta />
    </MarketingLayout>
  );
}

/* ---------------- About ---------------- */
export function AboutPage() {
  useSeo({ title: "About", path: "/about", description: "Why Residennce exists: to bring the business of real estate into one connected operating system." });
  return (
    <MarketingLayout>
      <Hero eyebrow="About" title="Why Residennce exists." />
      <Section className="py-16" data-testid="about">
        <div className="max-w-2xl space-y-4 text-lg text-charcoal/75">
          <p>Real estate agents are running real businesses — but the tools they use rarely treat them that way. Relationships live in one place, transactions in another, deadlines somewhere else entirely.</p>
          <p>Residennce was built to change that: to bring the operational side of a real estate business into one connected system, with intelligence built in. AgentHQ is our flagship product, and RESA — the Real Estate Strategic Assistant — is the intelligence woven throughout it.</p>
          <p>We're focused on the business of real estate: the running, selling, marketing, protecting, and growing that happens behind every great agent.</p>
        </div>
      </Section>
      <FinalCta />
    </MarketingLayout>
  );
}

/* ---------------- FAQ ---------------- */
function FaqInline({ limit }) {
  const items = limit ? FAQS.slice(0, limit) : FAQS;
  return (
    <div data-testid="faq">
      <h2 className="font-brand text-2xl font-bold text-brand-navy">Frequently asked questions</h2>
      <div className="mt-6 divide-y divide-black/10 rounded-2xl border border-black/10 bg-white">
        {items.map((f) => (
          <details key={f.q} className="group px-5 py-4" data-testid="faq-item">
            <summary className="flex cursor-pointer list-none items-center justify-between font-medium text-brand-navy">
              {f.q}<ChevronRight className="h-4 w-4 text-brand-gold transition-transform group-open:rotate-90" />
            </summary>
            <p className="mt-2 text-sm text-charcoal/70">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}
export function FaqPage() {
  useSeo({ title: "FAQ", path: "/faq", description: "Answers about AgentHQ, RESA, the free trial, Founding Agent pricing, and more." });
  return (
    <MarketingLayout>
      <Hero eyebrow="FAQ" title="Questions, answered." />
      <Section className="py-16"><FaqInline /></Section>
      <FinalCta />
    </MarketingLayout>
  );
}

/* ---------------- Legal & policy pages ---------------- */
const POLICY_UPDATED = "June 2026";

function PolicyPage({ kind, title, intro, sections }) {
  useSeo({ title, path: `/${kind}`, description: `${title} for Residennce and AgentHQ.` });
  return (
    <MarketingLayout>
      <Hero eyebrow="Legal" title={title} />
      <Section className="py-16" data-testid={`legal-${kind}`}>
        <div className="rounded-xl border border-brand-gold/40 bg-brand-gold/10 px-4 py-3 text-sm text-brand-navy" data-testid="policy-disclaimer">
          <Info className="mr-1.5 inline h-4 w-4" /> This is a Founder-approved operational policy written in plain language for launch readiness. It is not attorney-reviewed, legally certified, or a substitute for legal advice.
        </div>
        <p className="mt-6 text-xs uppercase tracking-widest text-charcoal/50">Last updated {POLICY_UPDATED}</p>
        {intro && <p className="mt-3 max-w-2xl text-sm text-charcoal/70">{intro}</p>}
        <div className="mt-8 max-w-2xl space-y-8">
          {sections.map((s) => (
            <div key={s.h}>
              <h2 className="font-brand text-lg font-bold text-brand-navy">{s.h}</h2>
              <div className="mt-2 space-y-2 text-sm leading-relaxed text-charcoal/70">
                {(s.p || []).map((para, i) => <p key={i}>{para}</p>)}
                {s.list && <ul className="list-disc space-y-1 pl-5">{s.list.map((li, i) => <li key={i}>{li}</li>)}</ul>}
              </div>
            </div>
          ))}
          <p className="text-sm text-charcoal/70">Questions about this policy? Contact <a href="mailto:support@residennce.com" className="text-brand-navy underline">support@residennce.com</a>.</p>
        </div>
      </Section>
    </MarketingLayout>
  );
}

const PRIVACY_SECTIONS = [
  { h: "Overview", p: ["This Privacy Policy explains how RESIDENNCE (\u201cResidennce\u201d, \u201cwe\u201d, \u201cus\u201d) handles information when you use AgentHQ and RESA. It applies to information processed through your Residennce account and workspace.", "By using AgentHQ you agree to the handling of information as described here."] },
  { h: "Information you provide", list: [
    "Account & profile information — your name, email, password (stored only as a secure hash), and profile details.",
    "Business information — your workspace settings, brand profile, and preferences.",
    "Contacts, leads, and customer information — the relationship and client records you enter into the CRM.",
    "Transaction and business records — deals, commissions, tasks, calendar entries, and related business data you create.",
    "Documents — files you upload and store in AgentHQ.",
    "Support communications — messages, tickets, and feedback you send us.",
  ] },
  { h: "Information collected automatically", list: [
    "Usage and activity information — actions within AgentHQ used to operate and improve the service.",
    "Cookies and session technology — a secure, HttpOnly session cookie is used to keep you signed in. We do not sell your information.",
  ] },
  { h: "How we use information", p: ["We use information to provide and maintain AgentHQ, authenticate your account, deliver the features you use, operate RESA, process your subscription, provide support, keep the service secure, and comply with applicable law."] },
  { h: "RESA and AI processing", p: ["RESA is an assistant built into AgentHQ. When you use RESA, relevant content from your request is processed by third-party AI providers to generate a response. RESA output can be inaccurate or incomplete and should be reviewed before you rely on it. Do not treat RESA output as legal, financial, tax, or brokerage advice."] },
  { h: "Payment processing", p: ["Subscription payments are processed by Stripe. Residennce does not store your full card details. Stripe processes your payment information under its own terms and privacy policy. Residennce stores your subscription status and related billing metadata to manage your access."] },
  { h: "Service providers", p: ["We use a limited set of service providers to run AgentHQ (for example, cloud hosting, payment processing, and AI providers that power RESA). These providers process information only to provide their services to us."] },
  { h: "Security", p: ["We take reasonable measures to protect your information. Access to AgentHQ is served over encrypted connections (HTTPS/TLS), session cookies are HttpOnly and Secure, passwords are stored only as secure hashes, access is role-based, and each workspace's data is logically isolated from other workspaces. No online service can guarantee absolute security."] },
  { h: "Data retention", p: ["We retain your account and business data while your account exists and as needed to operate the service, meet legal obligations, and resolve disputes. Cancelling a subscription does not by itself delete your business data. We do not promise indefinite or permanent retention, and data may be deleted in accordance with our applicable retention practices."] },
  { h: "Cancellation and account status", p: ["If you cancel your subscription, you keep access through the end of your paid period. Your account, workspace, and retained business data are not automatically deleted when a subscription is cancelled or expires. Entitlement to use AgentHQ and retention of your data are separate matters."] },
  { h: "Your privacy requests", p: ["You may request access to, correction of, or deletion of your information, subject to applicable law and our operational and legal retention needs. To make a request, contact us at support@residennce.com."] },
  { h: "Changes to this policy", p: ["We may update this Privacy Policy from time to time. Material changes will be reflected by updating the \u201cLast updated\u201d date above."] },
  { h: "Contact", p: ["AgentHQ and RESA are operated by Residennce. You can reach us about privacy at support@residennce.com or by mail at 9951 Atlantic Blvd., Ste 443, Jacksonville, FL 32225."] },
];

const TERMS_SECTIONS = [
  { h: "Acceptance & eligibility", p: ["These Terms of Service govern your use of AgentHQ and RESA, provided by RESIDENNCE. By creating an account or using the service you agree to these terms. You must be able to form a binding agreement and use AgentHQ for lawful, professional purposes."] },
  { h: "Your account & responsibility", p: ["You are responsible for your account, for keeping your credentials secure, and for all activity in your workspace. You are responsible for the accuracy and lawfulness of the data you enter, including contacts, leads, and client information."] },
  { h: "Subscription & access", p: ["AgentHQ is provided as a subscription (SaaS). Your access depends on an active subscription or an active free trial where offered. We may add, change, or remove features over time."] },
  { h: "Billing", p: ["Paid plans renew automatically each billing period until cancelled. Payments are processed by Stripe. Current standard pricing is $39/month for the standard monthly plan (annual pricing where offered). Applicable taxes may apply. You authorize us, through Stripe, to charge your payment method for each period until you cancel."] },
  { h: "Founding Agent pricing", p: ["The Founding Agent plan is $19/month and is limited to the first 100 qualifying paid Founding Agents. It includes everything in AgentHQ.", "The $19/month rate remains locked only while your qualifying subscription stays continuously active. If you schedule cancellation but reverse it before your paid period actually expires, your Founding Agent rate, number, and status are preserved. If your Founding Agent subscription actually expires, the $19/month rate is forfeited; your historical Founding Agent number and status are preserved for records, but returning later requires subscribing at the then-current standard price. A former Founding Agent does not regain $19 pricing merely because a slot later becomes available."] },
  { h: "Cancellation", p: ["You may cancel at any time, on a self-service basis, from your Billing settings. Cancellation takes effect at the end of your current paid billing period; you keep access until then. See our Cancellation Policy for details."] },
  { h: "Refunds", p: ["Subscription payments are non-refundable except where required by applicable law. There are no prorated or partial-month refunds, including when you cancel before the end of a paid period. See our Refund Policy for details."] },
  { h: "Acceptable use", p: ["You agree not to misuse AgentHQ, including by breaking the law, infringing others' rights, uploading malicious content, attempting to breach security or access other workspaces, or using the service to send unlawful communications."] },
  { h: "Your data & content", p: ["You retain your rights to the business data and content you put into AgentHQ. You grant us the limited rights needed to host and process that data to provide the service. You are responsible for having the right to store and process the information you enter."] },
  { h: "RESA and AI limitations", p: ["RESA uses AI and may produce inaccurate, incomplete, or unexpected output. You are responsible for reviewing and verifying RESA output before relying on it. RESA is a productivity tool, not a decision-maker."] },
  { h: "No professional advice", p: ["AgentHQ and RESA do not provide legal, financial, tax, brokerage, regulatory, or other professional advice. You are solely responsible for your professional obligations and compliance."] },
  { h: "Intellectual property", p: ["AgentHQ, RESA, the RESIDENNCE brand, and the software and materials we provide are owned by Residennce and its licensors. These terms do not transfer our intellectual property to you."] },
  { h: "Service availability", p: ["We work to keep AgentHQ available and reliable but do not guarantee uninterrupted or error-free service. Maintenance, updates, or factors outside our control may affect availability."] },
  { h: "Suspension & termination", p: ["We may suspend or terminate access for violations of these terms, non-payment, suspected abuse, or to protect the service. You may stop using the service at any time by cancelling your subscription."] },
  { h: "Disclaimers & limitation of liability", p: ["To the maximum extent permitted by law, AgentHQ and RESA are provided \u201cas is\u201d without warranties of any kind, and our liability is limited to the amount you paid for the service in the period giving rise to the claim. Some jurisdictions do not allow certain limitations, which may not apply to you."] },
  { h: "Changes to the service or terms", p: ["We may update AgentHQ and these terms from time to time. Material changes to the terms will be reflected by updating the \u201cLast updated\u201d date. Continued use after changes means you accept them."] },
  { h: "Governing law", p: ["These terms are governed by the laws of the State of Florida, United States, without regard to its conflict-of-laws rules, except where other applicable law requires otherwise. Residennce may update the governing jurisdiction in the future by revising this policy."] },
  { h: "Support & contact", p: ["AgentHQ and RESA are operated by Residennce, 9951 Atlantic Blvd., Ste 443, Jacksonville, FL 32225. For help or questions about these terms, contact support@residennce.com."] },
];

const CANCELLATION_SECTIONS = [
  { h: "Cancel anytime, self-service", p: ["You can cancel your AgentHQ subscription at any time directly from your Billing settings. You are not required to contact Support, send an email, submit a ticket, or obtain staff approval to cancel."] },
  { h: "When cancellation takes effect", p: ["Cancellation is scheduled for the end of your current paid billing period. Your subscription will not renew after that period. Cancelling does not immediately end your access."] },
  { h: "Access through your paid period", p: ["When cancellation is scheduled, you keep normal AgentHQ access through your paid-through date. After that date, no further renewal occurs and paid access ends."], list: [
    "Example: your paid period ends October 20 and you cancel on October 5.",
    "You keep AgentHQ access through October 20.",
    "You are not charged for the next period.",
    "You do not receive a refund for October 5\u201320.",
  ] },
  { h: "Reversing a scheduled cancellation", p: ["While your subscription is still active and has not actually expired, you can reverse a scheduled cancellation using Resume Subscription in Billing. This restores normal renewal without creating a new account, workspace, subscription identity, or Founding Agent slot."] },
  { h: "Founding Agent implications", p: ["If you are a Founding Agent, your $19/month rate remains protected through your current paid period. If you reverse the cancellation before expiration, your Founding Agent rate, number, and status are preserved. If your subscription actually expires, the $19/month rate is forfeited; returning later requires subscribing at the then-current standard price."] },
  { h: "Your account and data are not deleted", p: ["Cancellation does not automatically delete your account, workspace, or retained business data. Entitlement to use AgentHQ and retention of your data are handled separately, subject to our applicable retention practices."] },
  { h: "Contact", p: ["If you have trouble cancelling, contact support@residennce.com."] },
];

const REFUND_SECTIONS = [
  { h: "Subscriptions are non-refundable", p: ["Residennce subscription payments are non-refundable except where a refund is required by applicable law."] },
  { h: "No prorated or partial refunds", p: ["There are no prorated refunds for unused subscription time and no partial-month refunds. Cancelling before the end of your paid billing period does not entitle you to a refund for the remaining days."] },
  { h: "You keep access through your paid period", p: ["When you cancel, you retain normal AgentHQ access through the end of the period you have already paid for. You will not be charged for the next period."] },
  { h: "Prepaid periods", p: ["The same no-refund principle applies to prepaid subscription periods, subject to applicable law."] },
  { h: "Where refunds are required by law", p: ["Nothing in this policy limits any refund right you may have that cannot be waived under applicable law."] },
  { h: "Contact", p: ["Billing questions can be sent to support@residennce.com."] },
];

export function PrivacyPage() {
  return <PolicyPage kind="privacy" title="Privacy Policy"
    intro="How Residennce handles information across AgentHQ and RESA." sections={PRIVACY_SECTIONS} />;
}
export function TermsPage() {
  return <PolicyPage kind="terms" title="Terms of Service"
    intro="The terms that govern your use of AgentHQ and RESA." sections={TERMS_SECTIONS} />;
}
export function CancellationPolicyPage() {
  return <PolicyPage kind="cancellation-policy" title="Cancellation Policy"
    intro="How to cancel, when it takes effect, and what happens to your access and data." sections={CANCELLATION_SECTIONS} />;
}
export function RefundPolicyPage() {
  return <PolicyPage kind="refund-policy" title="Refund Policy"
    intro="Our policy on refunds for AgentHQ subscriptions." sections={REFUND_SECTIONS} />;
}

/* ---------------- shared blocks ---------------- */
function SeeInAction() {
  return <SeeInActionTour />;
}
function FinalCta() {
  return (
    <div className="bg-brand-navy text-white">
      <Section className="py-16 text-center">
        <h2 className="mx-auto max-w-xl font-brand text-3xl font-bold">Ready to run your business from one place?</h2>
        <p className="mx-auto mt-3 max-w-lg text-white/70">Start your 14-day free trial. Cancel anytime before it ends.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <TrialButton />
          <Link to="/login"><Button variant="outline" className="border-white/25 bg-white/5 text-white hover:bg-white/10">Login <ArrowRight className="h-4 w-4" /></Button></Link>
        </div>
      </Section>
    </div>
  );
}


/* ---------------- Contact Us ---------------- */
const INQUIRY_TYPES = [
  "AgentHQ / Product Question", "Founding Agent", "Billing Question",
  "Partnership", "Media / Speaking", "General Inquiry", "Other",
];
const SOCIALS = [
  { Icon: Instagram, label: "Instagram", handle: "@residennce" },
  { Icon: Facebook, label: "Facebook", handle: "@residennce" },
  { Icon: Youtube, label: "YouTube", handle: "@residennce" },
];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function ContactPage() {
  useSeo({ title: "Contact Us", path: "/contact", description: "Contact Residennce about AgentHQ, Founding Agent access, partnerships, media, or general inquiries." });
  const [form, setForm] = useState({ full_name: "", email: "", subject: "", inquiry_type: INQUIRY_TYPES[0], message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setErrors((x) => ({ ...x, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email address";
    if (!form.subject.trim()) e.subject = "Please enter a subject";
    if (!form.message.trim()) e.message = "Please enter a message";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await publicService.contact({ ...form, full_name: form.full_name.trim(), email: form.email.trim(), subject: form.subject.trim(), message: form.message.trim() });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please email support@residennce.com.");
    } finally {
      setBusy(false);
    }
  };

  const field = "mt-1.5 w-full";
  const errCls = (k) => (errors[k] ? "border-red-400 focus-visible:ring-red-400" : "");

  return (
    <MarketingLayout>
      <Hero eyebrow="Contact" title="Contact Residennce"
        sub="Have a question about AgentHQ, Founding Agent access, partnerships, or Residennce? Send us a message and our team will get back to you." />
      <Section className="py-16" data-testid="contact-page">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
          {/* Contact info */}
          <div>
            <h2 className="font-brand text-2xl font-bold text-brand-navy">Get in touch</h2>
            <a href="mailto:support@residennce.com" data-testid="contact-email"
              className="mt-5 flex items-center gap-3 rounded-xl border border-black/10 bg-brand-light-gray px-4 py-3 transition-colors hover:border-brand-gold/50">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-navy text-brand-gold"><Mail className="h-4 w-4" /></span>
              <span><span className="block text-xs font-bold uppercase tracking-widest text-charcoal/40">Email</span>
                <span className="text-sm font-semibold text-brand-navy">support@residennce.com</span></span>
            </a>
            <div className="mt-6">
              <p className="text-xs font-bold uppercase tracking-widest text-charcoal/40">Social</p>
              <ul className="mt-3 space-y-2.5" data-testid="contact-socials">
                {SOCIALS.map(({ Icon, label, handle }) => (
                  <li key={label} className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-full border border-brand-navy/15 bg-white text-brand-navy"><Icon className="h-4 w-4" /></span>
                    <span className="text-sm text-charcoal/80"><span className="font-medium text-brand-navy">{label}</span> · {handle}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Inquiry form */}
          <div className="rounded-2xl border border-black/10 bg-white p-6 sm:p-8" data-testid="contact-form-card">
            {done ? (
              <div className="flex flex-col items-center py-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="h-12 w-12 text-brand-gold" />
                <h3 className="mt-4 font-brand text-xl font-bold text-brand-navy">Thank you — your inquiry has been received.</h3>
                <p className="mt-2 max-w-sm text-sm text-charcoal/70">Our team will get back to you shortly at the email you provided.</p>
                <Button className="mt-6 bg-brand-navy text-white hover:bg-brand-deep-blue" data-testid="contact-reset"
                  onClick={() => { setForm({ full_name: "", email: "", subject: "", inquiry_type: INQUIRY_TYPES[0], message: "" }); setDone(false); }}>
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <h3 className="font-brand text-xl font-bold text-brand-navy">Submit an inquiry</h3>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="c-name" className="text-sm font-medium text-brand-navy">Full name</label>
                    <Input id="c-name" data-testid="contact-name" className={`${field} ${errCls("full_name")}`} value={form.full_name} onChange={set("full_name")} placeholder="Jordan Ellis" />
                    {errors.full_name && <p className="mt-1 text-xs text-red-500" data-testid="err-name">{errors.full_name}</p>}
                  </div>
                  <div>
                    <label htmlFor="c-email" className="text-sm font-medium text-brand-navy">Email address</label>
                    <Input id="c-email" type="email" data-testid="contact-email-input" className={`${field} ${errCls("email")}`} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                    {errors.email && <p className="mt-1 text-xs text-red-500" data-testid="err-email">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="c-subject" className="text-sm font-medium text-brand-navy">Subject</label>
                    <Input id="c-subject" data-testid="contact-subject" className={`${field} ${errCls("subject")}`} value={form.subject} onChange={set("subject")} placeholder="How can we help?" />
                    {errors.subject && <p className="mt-1 text-xs text-red-500" data-testid="err-subject">{errors.subject}</p>}
                  </div>
                  <div>
                    <label htmlFor="c-type" className="text-sm font-medium text-brand-navy">Inquiry type</label>
                    <select id="c-type" data-testid="contact-type" value={form.inquiry_type} onChange={set("inquiry_type")}
                      className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
                      {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="c-message" className="text-sm font-medium text-brand-navy">Message</label>
                  <Textarea id="c-message" data-testid="contact-message" rows={5} className={`${field} ${errCls("message")}`} value={form.message} onChange={set("message")} placeholder="Tell us a little about your inquiry…" />
                  {errors.message && <p className="mt-1 text-xs text-red-500" data-testid="err-message">{errors.message}</p>}
                </div>
                <Button type="submit" disabled={busy} data-testid="contact-submit"
                  className="mt-6 bg-brand-gold text-brand-navy hover:bg-brand-soft-gold">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Submit inquiry <ArrowRight className="h-4 w-4" /></>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
